export type ClientSummary = {
  name: string;
  slug: string;
  market: string;
  proposalCount: number;
  nextActionLabel: string;
  nextActionRoute: InternalRoute;
  activeMonth?: string;
  statusSummary: string;
  attentionCount?: number;
  isDemo?: boolean;
};

export type InternalRoute =
  | "/"
  | "/client/$clientSlug"
  | "/client/$clientSlug/$month/calendar"
  | "/client/$clientSlug/$month/briefing"
  | "/client/$clientSlug/$month/design"
  | "/client/$clientSlug/$month/client-review"
  | "/company"
  | "/workspace"
  | "/briefing"
  | "/design"
  | "/client-review";

export type MonthlyProposalStatus =
  | "Not started"
  | "Calendar in progress"
  | "Briefing"
  | "Design"
  | "In client review"
  | "Approved";

export type ProposalWeekTheme = {
  week: number;
  title?: string;
  holidayLabels: string[];
};

export type MonthlyProposalSummary = {
  id: string;
  clientSlug: string;
  clientName: string;
  month: string;
  monthLabel: string;
  status: MonthlyProposalStatus;
  nextAction: string;
  route: InternalRoute;
  stats: string;
  existing: boolean;
  requiresResetTypedConfirmation?: boolean;
  updatedAt?: number;
  approvedAt?: number;
  weeks?: ProposalWeekTheme[];
  contentPieceCount?: number;
  attentionCounts?: {
    readyToSend: number;
    changesWaiting: number;
    updating: number;
    updateFailed: number;
  };
  attentionPieceIds?: {
    readyToSend: string[];
    changesWaiting: string[];
    updating: string[];
    updateFailed: string[];
  };
  monthState?: {
    status: "living";
    furthestStage: "ideation" | "briefing" | "design" | "client_delivery" | "approved";
  };
};

export type LivingMonthFurthestStage = NonNullable<MonthlyProposalSummary["monthState"]>["furthestStage"];

/**
 * A living month keeps the furthest active Workspace Stage as its user-facing
 * proposal state. This prevents a replacement send from rewriting a client-
 * approved month back to Briefing while the affected piece is recovering.
 */
export function projectLivingProposalStatus(
  persistedStatus: MonthlyProposalStatus,
  furthestStage: LivingMonthFurthestStage | undefined,
): MonthlyProposalStatus {
  if (!furthestStage || furthestStage === "ideation") return persistedStatus;
  if (furthestStage === "briefing") return "Briefing";
  if (furthestStage === "design") return "Design";
  if (furthestStage === "client_delivery") return "In client review";
  if (furthestStage === "approved") return "Approved";
  return persistedStatus;
}

/**
 * The designed state vocabulary. Every surface that shows a month's state reads
 * it from here rather than printing the backend string, so the rack, the badge,
 * and any future portfolio view cannot describe the same month differently.
 *
 * `band` is the strip board's colour encoding and is deliberately narrower than
 * the status list: colour is spent only where work is live.
 */
export type ProposalStateTone = "idle" | "active" | "waiting" | "done";
export type ProposalStateBand = "none" | "live" | "done";
export type ProposalStateActor = "agency" | "client" | "none";

export type ProposalStateDescriptor = {
  status: MonthlyProposalStatus;
  label: string;
  tone: ProposalStateTone;
  band: ProposalStateBand;
  nextActor: ProposalStateActor;
  /** The verb the one primary control carries. Never the same string as `label`. */
  action: string;
};

const proposalStates: Record<MonthlyProposalStatus, ProposalStateDescriptor> = {
  "Not started": { status: "Not started", label: "Not started", tone: "idle", band: "none", nextActor: "none", action: "Create this month" },
  "Calendar in progress": { status: "Calendar in progress", label: "Calendar", tone: "active", band: "live", nextActor: "agency", action: "Continue calendar" },
  Briefing: { status: "Briefing", label: "Briefing", tone: "active", band: "live", nextActor: "agency", action: "Continue briefing" },
  Design: { status: "Design", label: "Design", tone: "active", band: "live", nextActor: "agency", action: "Continue design" },
  "In client review": { status: "In client review", label: "In client review", tone: "waiting", band: "live", nextActor: "client", action: "Open client review" },
  Approved: { status: "Approved", label: "Approved", tone: "done", band: "done", nextActor: "none", action: "View approved plan" },
};

export function describeProposalState(status: MonthlyProposalStatus): ProposalStateDescriptor {
  return proposalStates[status] ?? proposalStates["Not started"];
}

export function isCurrentProposalMonth(
  month: string,
  currentMonth = formatProposalMonthValue(new Date().getFullYear(), new Date().getMonth() + 1),
): boolean {
  return month === currentMonth;
}

export type ProposalWeekLine = {
  week: number;
  label: string;
  themed: boolean;
  holidayLabels: string[];
};

/** A month spans four to six ISO weeks; render the real count, never a padded six. */
export function buildProposalWeekLines(weeks: ProposalWeekTheme[] | undefined): ProposalWeekLine[] {
  if (!weeks?.length) return [];
  return [...weeks]
    .sort((left, right) => left.week - right.week)
    .map((entry) => ({
      week: entry.week,
      label: entry.title?.trim() ? entry.title.trim() : "No theme yet",
      themed: Boolean(entry.title?.trim()),
      holidayLabels: entry.holidayLabels ?? [],
    }));
}

export const demoClientSlugs = ["wiley-gourmet-supermarket"] as const;

export const suggestedProposalMonths = ["2026-09"] as const;

export type ProposalWorkflowKind = "notStarted" | "inProgress" | "approved";

export function formatProposalMonthValue(year: number, monthNumber: number): string {
  return `${year}-${String(monthNumber).padStart(2, "0")}`;
}

export function formatProposalMonth(month: string): string {
  const [year, monthNumber] = month.split("-");
  const date = new Date(Number(year), Number(monthNumber) - 1, 1);
  return new Intl.DateTimeFormat("en", { month: "long", year: "numeric" }).format(date);
}

export function formatProposalMonthShort(month: string): string {
  const [year, monthNumber] = month.split("-");
  const date = new Date(Number(year), Number(monthNumber) - 1, 1);
  return new Intl.DateTimeFormat("en", { month: "short", year: "numeric" }).format(date);
}

export function mapPlanStatus(status?: string): MonthlyProposalStatus {
  if (!status) {
    return "Not started";
  }
  if (status === "client_approved" || status === "closed") {
    return "Approved";
  }
  if (status === "client_review") {
    return "In client review";
  }
  if (status === "assets_ready") {
    return "Design";
  }
  if (status === "briefs_ready") {
    return "Briefing";
  }
  return "Calendar in progress";
}

export function nextRouteForStatus(status: MonthlyProposalStatus): InternalRoute {
  if (status === "Approved" || status === "In client review") {
    return "/client/$clientSlug/$month/client-review";
  }
  if (status === "Design") {
    return "/client/$clientSlug/$month/design";
  }
  if (status === "Briefing") {
    return "/client/$clientSlug/$month/briefing";
  }
  return "/client/$clientSlug/$month/calendar";
}

export function nextActionForStatus(status: MonthlyProposalStatus): string {
  if (status === "Not started") {
    return "Create plan";
  }
  if (status === "Approved") {
    return "View approved plan";
  }
  if (status === "In client review") {
    return "Open client review";
  }
  if (status === "Design") {
    return "Continue design";
  }
  if (status === "Briefing") {
    return "Continue briefing";
  }
  return "Continue calendar";
}

export function buildProposalStats(input: {
  existing: boolean;
  status: MonthlyProposalStatus;
  contentPieceCount?: number;
  deckVersionCount?: number;
}): string {
  if (!input.existing) {
    return "No proposal created yet";
  }
  if (input.status === "Approved") {
    return `${input.contentPieceCount ?? 0} content pieces · deck v${input.deckVersionCount ?? 0} approved`;
  }
  if (input.status === "In client review") {
    return `${input.contentPieceCount ?? 0} content pieces · in client review`;
  }
  if (input.status === "Design") {
    return `${input.contentPieceCount ?? 0} content pieces · assets in progress`;
  }
  if (input.status === "Briefing") {
    return `${input.contentPieceCount ?? 0} content pieces · briefs in progress`;
  }
  return "Themes and idea placements in progress";
}

export function createLocalClientFallback(): ClientSummary[] {
  return [
    {
      name: "Wiley Gourmet Supermarket",
      slug: "wiley-gourmet-supermarket",
      market: "MA",
      proposalCount: 1,
      nextActionLabel: "Open client",
      nextActionRoute: "/client/$clientSlug",
      activeMonth: "2026-09",
      statusSummary: "September is in client review",
    },
  ];
}

export function createLocalProposalFallback(clientSlug: string = demoClientSlugs[0]): MonthlyProposalSummary[] {
  const clientName = clientSlug === "wiley-gourmet-supermarket" ? "Wiley Gourmet Supermarket" : "Local client";

  return suggestedProposalMonths.map((month) => {
    const status: MonthlyProposalStatus = clientSlug === "wiley-gourmet-supermarket" ? "In client review" : "Not started";
    const existing = status !== "Not started";
    return {
      id: `${clientSlug}-${month}`,
      clientSlug,
      clientName,
      month,
      monthLabel: formatProposalMonth(month),
      status,
      nextAction: nextActionForStatus(status),
      route: nextRouteForStatus(status),
      stats: buildProposalStats({
        existing,
        status,
        contentPieceCount: status === "In client review" ? 13 : 0,
        deckVersionCount: status === "In client review" ? 2 : 0,
      }),
      existing,
      updatedAt: Date.UTC(2026, Number(month.slice(5, 7)) - 1, 1),
    };
  });
}

export function getProposalWorkflowKind(
  proposal: Pick<MonthlyProposalSummary, "existing" | "status">,
): ProposalWorkflowKind {
  if (!proposal.existing || proposal.status === "Not started") {
    return "notStarted";
  }
  if (proposal.status === "Approved") {
    return "approved";
  }
  return "inProgress";
}

export function buildProposalMonthGrid(
  proposals: MonthlyProposalSummary[],
  year = inferProposalYear(proposals),
): MonthlyProposalSummary[] {
  if (!proposals.length) {
    return [];
  }

  const proposalByMonth = new Map(proposals.map((proposal) => [proposal.month, proposal]));
  const fallback = proposals[0];
  const clientSlug = fallback.clientSlug;
  const clientName = fallback.clientName;

  return Array.from({ length: 12 }, (_, index) => {
    const month = formatProposalMonthValue(year, index + 1);
    const existing = proposalByMonth.get(month);
    if (existing) {
      return existing;
    }

    const status: MonthlyProposalStatus = "Not started";
    return {
      id: `${clientSlug}-${month}`,
      clientSlug,
      clientName,
      month,
      monthLabel: formatProposalMonth(month),
      status,
      nextAction: nextActionForStatus(status),
      route: nextRouteForStatus(status),
      stats: buildProposalStats({ existing: false, status }),
      existing: false,
    };
  });
}

export type ProposalYearGroup = {
  year: number;
  proposals: MonthlyProposalSummary[];
};

/** Keep the client-home index usable for histories spanning multiple years. */
export function groupProposalMonthsByYear(
  proposals: MonthlyProposalSummary[],
): ProposalYearGroup[] {
  const groups = new Map<number, MonthlyProposalSummary[]>();
  for (const proposal of [...proposals].sort((left, right) => left.month.localeCompare(right.month))) {
    const year = Number(proposal.month.slice(0, 4));
    const bucket = groups.get(year) ?? [];
    bucket.push(proposal);
    groups.set(year, bucket);
  }
  return [...groups.entries()]
    .sort(([left], [right]) => right - left)
    .map(([year, groupedProposals]) => ({ year, proposals: groupedProposals }));
}

export function getDefaultSelectedProposalMonth(
  proposals: MonthlyProposalSummary[],
  currentMonth = formatProposalMonthValue(new Date().getFullYear(), new Date().getMonth() + 1),
): string | undefined {
  const sorted = [...proposals].sort((left, right) => right.month.localeCompare(left.month));
  const latestExistingMonth = sorted.find((proposal) => proposal.existing)?.month;
  const nextNotStartedMonth = latestExistingMonth
    ? [...proposals]
        .filter((proposal) => getProposalWorkflowKind(proposal) === "notStarted" && proposal.month > latestExistingMonth)
        .sort((left, right) => left.month.localeCompare(right.month))[0]
    : undefined;

  // Oldest-first, so the not-started fallbacks below reach for the nearest
  // upcoming month rather than the far end of the year.
  const ascending = [...proposals].sort((left, right) => left.month.localeCompare(right.month));
  const notStarted = ascending.filter((proposal) => getProposalWorkflowKind(proposal) === "notStarted");

  return (
    proposals.find((proposal) => proposal.month === currentMonth && getProposalWorkflowKind(proposal) === "inProgress") ??
    sorted.find((proposal) => getProposalWorkflowKind(proposal) === "inProgress") ??
    nextNotStartedMonth ??
    notStarted.find((proposal) => proposal.month === currentMonth) ??
    notStarted.find((proposal) => proposal.month > currentMonth) ??
    notStarted[0] ??
    sorted.find((proposal) => getProposalWorkflowKind(proposal) === "approved")
  )?.month;
}

export function getProposalAgingCue(
  proposal: MonthlyProposalSummary,
  currentMonth = formatProposalMonthValue(new Date().getFullYear(), new Date().getMonth() + 1),
) {
  if (proposal.month >= currentMonth || getProposalWorkflowKind(proposal) !== "inProgress") return undefined;
  return `Past month - still in ${proposal.status.replace(" in progress", "")}`;
}

function inferProposalYear(proposals: MonthlyProposalSummary[]): number {
  const sortedMonths = [...proposals].map((proposal) => proposal.month).sort();
  const newestMonth = sortedMonths[sortedMonths.length - 1];
  const year = Number(newestMonth?.slice(0, 4));
  return Number.isFinite(year) ? year : 2026;
}

export function getNextStartableProposal(proposals: MonthlyProposalSummary[]): MonthlyProposalSummary | undefined {
  const unstarted = proposals
    .filter((proposal) => !proposal.existing)
    .sort((left, right) => left.month.localeCompare(right.month));
  if (!unstarted.length) {
    return undefined;
  }

  const existingMonths = proposals
    .filter((proposal) => proposal.existing)
    .map((proposal) => proposal.month)
    .sort();
  const latestExistingMonth = existingMonths[existingMonths.length - 1];
  if (latestExistingMonth) {
    return unstarted.find((proposal) => proposal.month > latestExistingMonth);
  }

  return unstarted[0];
}

export function isProposalActionBusy(
  busyProposalId: string | undefined,
  proposal: Pick<MonthlyProposalSummary, "id"> | undefined,
): boolean {
  return Boolean(proposal && busyProposalId === proposal.id);
}
